import React, { useState, useMemo } from 'react';
import {
  Stack,
  Heading,
  Icon,
  Box,
  FormControl,
  Textarea,
  Button,
} from '@chakra-ui/react';

import StepWrapper from '../StepWrapper';
import {
  FaDollarSign,
  FaClipboardList,
  FaImage,
  FaStickyNote,
  FaCheck,
  FaCheckCircle,
} from 'react-icons/fa';
import { IoLocationOutline } from 'react-icons/io5';
import { AiFillCheckCircle, AiFillCloseCircle } from 'react-icons/ai';
import {
  ItemAskModel,
  ItemModel,
  UserModel,
  ItemBidModel,
  AddressModel,
} from 'models';
import { Condition } from 'models/types';
import { ItemBidStatus } from 'models/Item/ItemBid/types';
import Wizard from '../Wizard';
import ProgressBar from '../../ProgressBar/ProgressBar';
import ItemAskForm, { ItemAskFormData } from '../../Form/ItemAskForm/ItemAskForm';
import { ItemAskAdditionalDataFormData } from 'components/Form/ItemAskAdditionalDataForm/ItemAskAdditionalDataForm';
import AddressDropdown from '../../AddressDropdown/AddressDropdown';
import ItemAskEditCard from '../../Item/ItemAskEditCard/ItemAskEditCard';
import UnauthorizedAccess from '../../UnauthorizedAccess/UnauthorizedAccess';
import PaymentBreakdown from '../../PaymentBreakdown/PaymentBreakdown';
import FilepondUploader from '../../Uploader/FilepondUploader/FilepondUploader';
import ConditionDropdown from '../../ConditionDropdown/ConditionDropdown';
import { getAskEditPayload } from 'utils/itemask';
import { JSONObject } from 'models/Api/types';

interface ItemAskEditWizardProps {
  item: ItemModel;
  itemAsk: ItemAskModel;
  user: UserModel;
  onSubmit?: (
    itemAsk: ItemAskModel,
    editData: JSONObject
  ) => void;
  isLoading?: boolean;
  hasErrors?: boolean;
  onAddAddressClick?: () => void;
}

export default function ItemAskEditWizard({
  item,
  itemAsk,
  user,
  onAddAddressClick,
  onSubmit = () => { },
  isLoading = false,
  hasErrors = false,
}: ItemAskEditWizardProps) {
  const MAX_IMAGE_NUMBERS = 6;
  const [activeStep, setActiveStep] = useState(0);
  const [askData, setAskData] = useState<ItemAskFormData>({
    askPrice: itemAsk.amount,
    askExpiration: itemAsk.expirationDays,
  });
  const [additionalAskData, setAdditionalAskData] = useState<ItemAskAdditionalDataFormData>({
    condition: itemAsk.condition || Condition.GOOD,
    notes: itemAsk.notes || '',
    address: itemAsk.address ||
      new AddressModel({ line1: '', line2: '', city: '', state: '', postal_code: '', country: '' }),
    images: itemAsk.images || [],
    useEscrow: itemAsk.useEscrow
  });
  const [isUploading, setIsUploading] = useState(false);

  const opposingBidTransaction = useMemo(
    () =>
      new ItemBidModel({
        id: '',
        item_id: item.id,
        user_id: user.id,
        amount: askData.askPrice || itemAsk.amount,
        currency: 'USD',
        status: ItemBidStatus.PENDING,
        condition: additionalAskData.condition || itemAsk.condition || Condition.GOOD,
        notes: additionalAskData.notes || '',
        expiration_days: 30,
        verified: itemAsk.isVerified() || false,
        use_escrow: additionalAskData.useEscrow,
      }),
    [user, item.id, askData.askPrice, additionalAskData.condition, itemAsk, additionalAskData.notes, additionalAskData.useEscrow]
  );

  const onWizardSubmit = () => {
    const editPayload = getAskEditPayload(itemAsk, askData, additionalAskData);
    onSubmit(itemAsk, editPayload);
  };

  const handleAmountChange = (data: ItemAskFormData) => {
    setAskData(data);
  };

  const handleConditionChange = (cond: Condition) => {
    setAdditionalAskData((prev) => ({ ...prev, condition: cond }));
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAdditionalAskData((prev) => ({ ...prev, notes: e.target.value }));
  };

  const handleAddressChange = (updated: AddressModel) => {
    setAdditionalAskData((prev) => ({ ...prev, address: updated }))
  };

  const handleImageAdd = (url: string) => {
    setAdditionalAskData(prev => ({
      ...prev,
      images: [url, ...prev.images ?? []]
    }));
  };

  const handleImageRemove = (url: string) => {
    setAdditionalAskData(prev => ({
      ...prev,
      images: prev.images?.filter(u => u !== url),
    }));
  };

  const handleEscrowToggle = (checked: boolean) => {
    setAdditionalAskData((prev) => ({ ...prev, useEscrow: checked }))
  };

  function renderItemEditCard() {
    return (
      <ItemAskEditCard
        item={item}
        originalAmount={itemAsk.amount}
        currentAmount={askData.askPrice}
        createdDaysAgo={itemAsk.getCreatedDaysAgo()}
        updatedDaysAgo={itemAsk.getUpdatedDaysAgo()}
        updated={itemAsk.hasBeenUpdated()}
        expired={itemAsk.isExpired()}
        noLongerExpired={(askData.askExpiration || 0) > 0}
        condition={additionalAskData.condition || itemAsk.getCondition()}
        countryCode={additionalAskData.address?.country || itemAsk.getCountryCode()}
        askImages={additionalAskData.images || itemAsk.images}
      />
    );
  }

  function renderAmountStep() {
    if (itemAsk.belongsToUser(user)) {
      if (user.isSellerVerified()) {
        return (
          <Stack spacing={4}>
            {renderItemEditCard()}
            <StepWrapper
              stepKey="amount"
              icon={FaDollarSign}
              title="Ask Amount"
              description="Update your asking price"
              minH="320px"
            >
              <ItemAskForm
                item={item}
                onChange={handleAmountChange}
                defaultFormData={askData}
                showSubmit={false}
                showItemCard={false}
                showExpirationDropdown={false}
              />
            </StepWrapper>
          </Stack>
        );
      } else if (user.hasStripeConnectedAccountId()) {
        return (
          <UnauthorizedAccess
            title="Unauthorized"
            description="Please Complete Seller Onboarding To Be Eligible to Place An Ask"
            buttonText="Complete Onboarding"
            onClick={() => { }}
          />
        );
      }

      return (
        <UnauthorizedAccess
          title="Unauthorized"
          description="Please Activate Selling In Order To Be Eligible To Place An Ask"
          buttonText="Activate Selling"
          onClick={() => { }}
        />
      );
    }

    return (
      <UnauthorizedAccess
        title="Unauthorized"
        description="You are unauthorized to edit this ask."
      />
    );
  }

  function renderConditionStep() {
    return (
      <Stack spacing={4}>
        {renderItemEditCard()}
        <StepWrapper
          stepKey="condition"
          icon={FaClipboardList}
          title="Item Condition"
          description="Update the item's condition"
          minH="320px"
        >
          <ConditionDropdown
            defaultValue={additionalAskData.condition}
            onChange={handleConditionChange}
            size="sm"
          />
        </StepWrapper>
      </Stack>
    );
  }

  function renderImageStep() {
    return (
      <Stack spacing={4}>
        {renderItemEditCard()}
        <Stack minH="320px">
          <StepWrapper
            stepKey="images"
            icon={FaImage}
            title="Upload Images"
            description={`Update Your Item Images (Max ${MAX_IMAGE_NUMBERS})`}
            minH="fit-content"
          >
            <Box />
          </StepWrapper>
          <FilepondUploader
            initialFileUrls={additionalAskData.images}
            onUploadingChange={setIsUploading}
            onFileProcess={handleImageAdd}
            onFileRevert={handleImageRemove}
            maxFiles={MAX_IMAGE_NUMBERS}
            label="Drag & Drop your Image"
          />

        </Stack>
      </Stack>
    );
  }

  function renderNotesStep() {
    return (
      <Stack spacing={4}>
        {renderItemEditCard()}
        <StepWrapper
          stepKey="notes"
          icon={FaStickyNote}
          title="Add Notes"
          description="Update any noteworthy additional details"
          minH="320px"
        >
          <FormControl>
            <Textarea value={additionalAskData.notes} onChange={handleNotesChange} placeholder="Enter notes" size="sm" />
          </FormControl>
        </StepWrapper>
      </Stack>
    );
  }

  function renderAddressConfirmStep() {
    const userAddresses = [
      ...(user.getPrimaryAddress() ? [user.getPrimaryAddress() as AddressModel] : []),
      ...user.address.secondaryAddresses,
    ];

    return (
      <Stack spacing={4}>
        {renderItemEditCard()}
        <StepWrapper
          stepKey="addressConfirm"
          icon={IoLocationOutline}
          title="Pickup Address"
          description="Update your pickup address"
          minH="320px"
        >
          <Stack gap={4} alignItems="center" width="100%">
            <AddressDropdown addresses={userAddresses} defaultAddress={additionalAskData?.address} onSelect={handleAddressChange} onAddAddress={onAddAddressClick} size="sm" />
          </Stack>
        </StepWrapper>
      </Stack>
    );
  }

  function renderConfirmationStep() {
    return (
      <Stack spacing={4}>
        {renderItemEditCard()}
        <StepWrapper
          stepKey="confirm"
          icon={FaCheckCircle}
          title="Confirm Updated Ask"
          minH="320px"
        >
          <Stack spacing={4}>
            <PaymentBreakdown transaction={opposingBidTransaction} type="bid" useEscrow={!!additionalAskData.useEscrow} onToggleEscrow={handleEscrowToggle} showInfoGuide={false} showTransactionCard={false} />
          </Stack>
        </StepWrapper>
      </Stack>
    );
  }

  function renderWizardFinishingSuccessStep() {
    const content = hasErrors ? (
      <Stack gap={4} alignItems="center">
        <Icon as={AiFillCloseCircle} color="red.500" w={10} h={10} />
        <Heading size="xs" textAlign="center">
          Your Ask Could Not Be Updated.
          <br />Please Try Again
        </Heading>
      </Stack>
    ) : (
      <Stack gap={4} alignItems="center">
        <Icon as={AiFillCheckCircle} color="green.500" w={10} h={10} />
        <Heading size="xs" textAlign="center">
          Your Ask Has Been Updated
        </Heading>
      </Stack>
    );

    return <Box height={{ base: 'auto', md: 340 }}>{content}</Box>;
  }

  const wizardSteps = [
    { label: '', icon: FaDollarSign, content: renderAmountStep(), requiresValidation: true, isValidated: (askData?.askPrice || 0) > 0 },
    { label: '', icon: FaClipboardList, content: renderConditionStep(), requiresValidation: false, isValidated: true },
    { label: '', icon: FaImage, content: renderImageStep(), requiresValidation: true, isValidated: !isUploading },
    { label: '', icon: FaStickyNote, content: renderNotesStep(), requiresValidation: false, isValidated: true },
    { label: '', icon: IoLocationOutline, content: renderAddressConfirmStep(), requiresValidation: true, isValidated: !!additionalAskData?.address?.line1 },
    { label: '', icon: FaCheckCircle, content: renderConfirmationStep(), requiresValidation: true, isValidated: true },
  ];

  const totalSteps = wizardSteps.length;
  const progressPercentage = ((activeStep + 1) / totalSteps) * 100;
  const progressIcon = activeStep === totalSteps - 1 ? FaCheck : wizardSteps[activeStep].icon;

  return (
    <Stack>
      <ProgressBar progress={progressPercentage} icon={progressIcon} dynamicColor height={4} />
      <Wizard
        steps={wizardSteps}
        finishStep={renderWizardFinishingSuccessStep()}
        loading={isLoading}
        showStepper={false}
        onStepChange={setActiveStep}
        onSubmit={onWizardSubmit}
      />
    </Stack>
  );
}
